import { TaskStateTransitionResource } from 'dy-frontend-http-repository/lib/modules/Task/resources';
import { TaskSubTicketStatusLogResource } from 'dy-frontend-http-repository/lib/modules/TaskSubTicket/resources';

import { TaskQueueStateTransitionResource } from '@pages/Task/data/types';

// The event kinds stay DISCRIMINATED all the way to the renderer. A parent Request state and
// a sub-task status are different types describing different things, and the one failure mode
// that would make this feature actively wrong is rendering a sub-task status as if the Request
// itself had moved into it. A union with a literal tag makes that a type error rather than a
// judgement call at the call site.
export interface RequestTimelineRequestEntry {
    kind: 'request';
    at: string;
    index: number;
    transition: TaskStateTransitionResource;
}

export interface RequestTimelineSubTaskEntry {
    kind: 'sub_task';
    at: string;
    index: number;
    log: TaskSubTicketStatusLogResource;
}

// A Request queue/pause change. Its own vocabulary (Active / Backlog / Paused), never a workflow
// state and never a sub-task status, so it stays a third tag in the union for the same reason.
export interface RequestTimelineQueueEntry {
    kind: 'queue';
    at: string;
    index: number;
    transition: TaskQueueStateTransitionResource;
}

export type RequestTimelineEntry =
    | RequestTimelineRequestEntry
    | RequestTimelineSubTaskEntry
    | RequestTimelineQueueEntry;

export interface MergeRequestTimelineParams {
    // Parent Request transitions, newest first, exactly as the existing endpoint returns them
    transitions: TaskStateTransitionResource[];

    // Sub-task status transitions, newest first
    statusLogs: TaskSubTicketStatusLogResource[];

    // Request queue/pause transitions, newest first. Optional so every existing caller is a
    // strict no-op, exactly as an empty sub-task list is
    queueTransitions?: TaskQueueStateTransitionResource[];
}

// Merges the parent Request's own state transitions with its sub-tasks' status transitions into
// one descending timeline.
//
// Parent entries are passed through untouched: this adds rows beside them, it never rewrites or
// reinterprets one. An empty sub-task list is therefore a strict no-op — the panel renders
// exactly what it rendered before this existed, which is what makes the feature safe to ship on
// every Request rather than only on the ones with phases.
//
// Ties are broken on arrival index, so a Request transition and a sub-task transition committed
// inside the same second do not swap places between two renders of identical data.
export const mergeRequestTimeline = ({
    transitions,
    statusLogs,
    queueTransitions = [],
}: MergeRequestTimelineParams): RequestTimelineEntry[] => {
    const requestEntries: RequestTimelineEntry[] = transitions.map((transition, position) => ({
        kind: 'request',
        at: transition.applied_at,
        index: position,
        transition,
    }));

    // Offset past the parent entries so the tie-break is a total order across BOTH lists rather
    // than two independent 0..n sequences that collide on every tie
    const subTaskEntries: RequestTimelineEntry[] = statusLogs.map((log, position) => ({
        kind: 'sub_task',
        at: log.created_at,
        index: transitions.length + position,
        log,
    }));

    // Offset past BOTH existing lists, for the same reason: one total order across all three
    const queueEntries: RequestTimelineEntry[] = queueTransitions.map((transition, position) => ({
        kind: 'queue',
        at: transition.applied_at,
        index: transitions.length + statusLogs.length + position,
        transition,
    }));

    const merged = [...requestEntries, ...subTaskEntries, ...queueEntries];

    merged.sort((a, b) => {
        const aAt = Date.parse(a.at);
        const bAt = Date.parse(b.at);

        if (aAt !== bAt) {
            return bAt - aAt;
        }

        return a.index - b.index;
    });

    return merged;
};
